import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { PropertyCalendar, PropertyCalendarRequest } from '../../../models/property.model';
import { PropertyService } from '../../../services/property.service';
import { SnackBarService } from '../../../../../shared/services/snackbar.service';
import { Subject, takeUntil } from 'rxjs';

// -----------------------------------------------------------------
// Typed form
interface CalendarForm {
  id: FormControl<string>;
  name: FormControl<string>;
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
  costPerNight: FormControl<number>;
  costPerWeek: FormControl<number>;
  costPerMonth: FormControl<number>;
}

interface DialogData {
  propertyId: string;
  currentCalendars: PropertyCalendar[];
}

// -----------------------------------------------------------------
@Component({
  selector: 'app-calendars-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './calendars-edit.html',
  styleUrl: './calendars-edit.scss',
})
export class CalendarsEdit implements AfterViewInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<CalendarsEdit>);
  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly propertyService = inject(PropertyService);
  private readonly snackbar = inject(SnackBarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Output() save = new EventEmitter<PropertyCalendar[]>();
  @Output() cancel = new EventEmitter<void>();

  form = new FormGroup({
    calendars: new FormArray<FormGroup<CalendarForm>>([]),
  });

  // -----------------------------------------------------------------
  ngAfterViewInit(): void {
    const calendars = Array.isArray(this.data.currentCalendars)
      ? this.data.currentCalendars
      : [];

    const array = this.form.controls.calendars as FormArray<FormGroup<CalendarForm>>;
    array.clear();
    calendars.forEach((cal) => array.push(this.createCalendarGroup(cal)));
    console.log(calendars);
    this.form.markAsPristine();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -----------------------------------------------------------------
  private createCalendarGroup(cal: PropertyCalendar): FormGroup<CalendarForm> {
    return new FormGroup<CalendarForm>({
      id: new FormControl<string>(cal.id ?? '', { nonNullable: true }),
      name: new FormControl<string>(cal.name, { nonNullable: true, validators: [Validators.required] }),
      startDate: new FormControl<Date | null>(
        cal.startDate ? new Date(cal.startDate) : null,
        { nonNullable: false, validators: [Validators.required] }
      ),
      endDate: new FormControl<Date | null>(
        cal.endDate ? new Date(cal.endDate) : null,
        { nonNullable: false, validators: [Validators.required] }
      ),
      costPerNight: new FormControl<number>(cal.costPerNight, {
        nonNullable: true,
        validators: [Validators.min(0)]
      }),
      costPerWeek: new FormControl<number>(cal.costPerWeek, {
        nonNullable: true,
        validators: [Validators.min(0)]
      }),
      costPerMonth: new FormControl<number>(cal.costPerMonth, {
        nonNullable: true,
        validators: [Validators.min(0)]
      }),
    });
  }

  get calendarControls(): FormGroup<CalendarForm>[] {
    return this.form.controls.calendars.controls as FormGroup<CalendarForm>[];
  }

  // -----------------------------------------------------------------
  addCalendar(): void {
    const newCal: PropertyCalendar = {
      id: '',
      propertyId: this.data.propertyId,
      name: 'New Calendar',
      startDate: new Date(),
      endDate: new Date(),
      costPerNight: 0,
      costPerWeek: 0,
      costPerMonth: 0,
      createdDate: new Date(),
      updatedDate: new Date(),
    };
    const array = this.form.controls.calendars as FormArray<FormGroup<CalendarForm>>;
    array.push(this.createCalendarGroup(newCal));
    this.form.markAsDirty();
    this.cdr.detectChanges();
  }

  removeCalendar(index: number): void {
    const array = this.form.controls.calendars as FormArray<FormGroup<CalendarForm>>;
    array.removeAt(index);
    this.form.markAsDirty();
    this.cdr.detectChanges();
  }

  // -----------------------------------------------------------------
  onSave(): void {
    if (this.form.invalid) {
      console.log('form invalid');
      this.snackbar.open('Please fix all errors', 'Close', {
        panelClass: ['error-snackbar'],
        duration: 4000,
      });
      return;
    }

    const calendars: PropertyCalendarRequest[] = this.calendarControls.map((g) => {
      const v = g.getRawValue();
      return {
        name: v.name,
        startDate: v.startDate!,
        endDate: v.endDate!,
        costPerNight: v.costPerNight,
        costPerWeek: v.costPerWeek,
        costPerMonth: v.costPerMonth
      };
    });

    this.propertyService
      .setPropertyCalendars(this.data.propertyId, calendars)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.propertyService.refresh(this.data.propertyId);
          this.save.emit([]);
          this.dialogRef.close(calendars);
          this.snackbar.open('✅ Calendars saved', ' ', {
            panelClass: ['success-snackbar'],
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
        error: (err) => {
          console.error('Save failed', err);
          this.snackbar.open('⚠️ Failed to save calendars', ' ', {
            panelClass: ['error-snackbar'],
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        },
      });
  }

  onCancel(): void {
    this.cancel.emit();
    this.dialogRef.close();
  }
}